<script lang="ts">
	import { getTranslate } from '@tolgee/svelte';
	import Button from './Button.svelte';
	import type { OrderLine } from '$lib/types/Order/order';
	import { cart } from '$lib/store/cart.store';
	import { createEventDispatcher } from 'svelte';
	import { GetSDSOnlyInfoStore } from '$houdini';

	import { languageStore } from '$lib/store/language.store';
	import { recordAddToCart } from '$lib/posthog';
	import { mapToNode } from '$lib/utils/product';
	import { addSDS } from '$lib/utils/sds';
	import { ensureCountry, ensureLocale } from '$lib/utils/i18n';
	import { isNetworkError } from '$lib/utils';

	export let variant: OrderLine['variant'];

	const store = new GetSDSOnlyInfoStore();
	$: sdsVariant = mapToNode($store.data?.sdsProduct?.variants).find(
		(sdsV) => sdsV.sku?.value === variant?.sku?.value
	);

	$: hasStock =
		$cart.getRealQuantity(
			variant?.sku?.value || '',
			Math.max(variant?.quantityAvailable || 0, sdsVariant?.quantityAvailable || 0)
		) > 0;

	let isAddingToCart: boolean = false;

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();

	const handleClick = async (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (variant && variant.sku?.value && sdsVariant) {
			recordAddToCart({
				sku: variant?.sku?.value || '',
				source: 'Order Again',
				quantity: 1,
				handle: variant?.product?.handle || ''
			});
			isAddingToCart = true;
			const lineId = await cart.addSDSItem({
				sku: variant.sku?.value || '',
				variantId: variant.id,
				sdsId: sdsVariant.id,
				sdsThreshold: Number(variant.sds_qty?.value || '10') || 10,
				quantity: 1,
				handle: variant?.product?.handle || '',
				sdsOnHand: sdsVariant.quantityAvailable || 0,
				avantradoOnHand: variant.quantityAvailable || 0
			});

			if (!lineId) {
				console.warn(
					'Could not add item to cart - did not generate lineId for variant: ',
					variant?.title,
					'with variantId: ',
					variant?.id
				);
			} else {
				dispatch('variantAddedToCart', { lineId: lineId });
			}
			isAddingToCart = false;
		}
	};

	let retries = 0;

	$: if (variant?.product.handle && retries < 3) {
		store
			.fetch({
				variables: {
					sdsHandle: addSDS(variant.product.handle),
					country: ensureCountry($languageStore.country),
					language: ensureLocale($languageStore.locale)
				}
			})
			.catch((e) => {
				if (isNetworkError(e)) {
					setTimeout(() => retries++, 2000 * (retries + 1));
					return;
				}
				throw e;
			});
	}
</script>

<Button
	class="px-4 -mr-4 py-2 !rounded hover:underline text-accent-primary text-h-12 disabled:opacity-60"
	on:click={handleClick}
	disabled={!variant || !hasStock}
	text={variant && hasStock ? $t('orders.reOrder') : $t('orders.reOrderNotPossible')}
	iconPosition="right"
>
	<div class="i-sprenger-repeat {isAddingToCart ? 'animate-spin' : ''}" slot="icon" />
</Button>
